import React, { useState, useEffect, useCallback } from 'react';
import {
  CardBody,
  Row,
  Col,
  Card,
  Container,
  CardHeader,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalBody,
  Label,
  Input,
} from 'reactstrap';
import DataTable from 'react-data-table-component';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import ActionButton from '../../../Components/Common/ActionButton';
import DeleteModal from '../../../Components/Common/DeleteModal';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { crudeApi, proHydroApi } from '../../../helpers/crude_api_helper';

// Backend entity: ReceptionFacility (06.WebApi.Crude, ReceptionFacilityController).
// Endpoints per api-contract-reception-facilities.md ({crudeHost}/v1):
//   list   GET    /v1/ReceptionFacility/GetListReceptionFacility  (paginated)
//   create POST   /v1/ReceptionFacility           (form data, NOT json)
//   update PUT    /v1/ReceptionFacility            (form data; id in body, not route)
//   delete DELETE /v1/ReceptionFacility/{id}
// `sortField` must be PascalCase and within the API whitelist: Id, PlantId,
// PlantName, FacilityName, MaxPourPoint, OrderNumber, Description, DataStatus.
// `dataStatus` is a string enum: 'Deleted' (never returned, list always
// filters IsDeleted), 'OnProcess' (pending review/approval — locks row
// actions), 'Active'.
const LIST_ENDPOINT = '/v1/ReceptionFacility/GetListReceptionFacility';
const ENDPOINT = '/v1/ReceptionFacility';

// Refinery Unit ("Plant") dropdown — proHydro host (06.WebApi), not the Crude
// host this page otherwise uses. ListResponse<RefineryUnitDto>, no pagination.
const REFINERY_UNIT_ENDPOINT = '/v1/RefineryUnits';

const emptyForm = {
  id: null,
  facilityName: '',
  plantId: '',
  maxPourPoint: '',
  orderNumber: '',
  description: '',
};

const toPascalCase = field =>
  field ? field.charAt(0).toUpperCase() + field.slice(1) : field;

const ReceptionFacilitiesView = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refineryUnitOptions, setRefineryUnitOptions] = useState([]);

  const [modalForm, setModalForm] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  // Server-side pagination / search / sort state.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  document.title = 'Reception Facilities';

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchKeyword.trim());
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const result = await crudeApi.get(LIST_ENDPOINT, {
        page,
        pageSize,
        searchText: debouncedSearch || undefined,
        sortField: sortField || undefined,
        sortOrder: sortField ? sortOrder : undefined,
      });
      setItems(Array.isArray(result?.items) ? result.items : []);
      setTotalCount(result?.totalCount || 0);
    } catch (error) {
      toast.error(`Failed to load data: ${error}`);
      setItems([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, debouncedSearch, sortField, sortOrder]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fetchRefineryUnits = async () => {
      try {
        const result = await proHydroApi.get(REFINERY_UNIT_ENDPOINT);
        const list = Array.isArray(result?.items) ? result.items : [];
        setRefineryUnitOptions(
          list
            .filter(unit => !unit.isDeleted)
            .sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0))
            .map(unit => ({
              id: unit.id,
              name: `${unit.plant} - ${unit.plantName}`,
            }))
        );
      } catch (error) {
        toast.error(`Failed to load Refinery Unit options: ${error}`);
        setRefineryUnitOptions([]);
      }
    };
    fetchRefineryUnits();
  }, []);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  // Client-side pre-check for a nicer UX; the backend's own uniqueness check
  // returns 409 Conflict on a duplicate facility name.
  const isDuplicate = () => {
    const name = (formData.facilityName || '').trim().toLowerCase();
    return items.some(
      row =>
        row.plantId === formData.plantId &&
        (row.facilityName || '').trim().toLowerCase() === name &&
        row.id !== formData.id
    );
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.facilityName || !formData.facilityName.trim()) {
      newErrors.facilityName = 'Facility Name is required';
    }
    if (!formData.plantId) {
      newErrors.plantId = 'Refinery Unit is required';
    }
    // Contract: orderNumber/maxPourPoint use NotEmpty() on a numeric type, so
    // 0 is rejected by the server — validate that explicitly here too.
    const maxPourPointNum = Number(formData.maxPourPoint);
    if (
      formData.maxPourPoint === '' ||
      formData.maxPourPoint === null ||
      formData.maxPourPoint === undefined
    ) {
      newErrors.maxPourPoint = 'Max Pour Point is required';
    } else if (maxPourPointNum === 0) {
      newErrors.maxPourPoint = 'Max Pour Point cannot be 0';
    }
    const orderNumberNum = Number(formData.orderNumber);
    if (
      formData.orderNumber === '' ||
      formData.orderNumber === null ||
      formData.orderNumber === undefined
    ) {
      newErrors.orderNumber = 'Order Number is required';
    } else if (orderNumberNum === 0) {
      newErrors.orderNumber = 'Order Number cannot be 0';
    }
    // Duplicate check only once the pair (Facility Name + Refinery Unit) is filled.
    if (formData.facilityName && formData.plantId && isDuplicate()) {
      newErrors.facilityName =
        'Facility Name already exists in this Refinery Unit';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNewModal = () => {
    setFormData(emptyForm);
    setErrors({});
    setModalForm(true);
  };

  const handleUpdateModal = row => {
    if (row.dataStatus !== 'Active') return; // locked while pending review/approval
    setFormData({
      id: row.id,
      facilityName: row.facilityName || '',
      plantId: row.plantId || '',
      maxPourPoint: row.maxPourPoint ?? '',
      orderNumber: row.orderNumber ?? '',
      description: row.description || '',
    });
    setErrors({});
    setModalForm(true);
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    setSaving(true);

    if (formData.id) {
      try {
        // Update: form-encoded PUT, id goes in the body (not the route).
        await crudeApi.putForm(ENDPOINT, {
          id: formData.id,
          plantId: formData.plantId,
          facilityName: formData.facilityName.trim(),
          orderNumber: formData.orderNumber,
          maxPourPoint: formData.maxPourPoint,
          description: formData.description || '',
        });
        toast.success('Reception Facility has been updated');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to update data: ${error}`);
      } finally {
        setSaving(false);
      }
    } else {
      try {
        await crudeApi.postForm(ENDPOINT, {
          plantId: formData.plantId,
          facilityName: formData.facilityName.trim(),
          orderNumber: formData.orderNumber,
          maxPourPoint: formData.maxPourPoint,
          description: formData.description || '',
        });
        toast.success('Reception Facility has been added');
        setModalForm(false);
        fetchData();
      } catch (error) {
        toast.error(`Failed to save data: ${error}`);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleDeleteModal = row => {
    if (row.dataStatus !== 'Active') return; // locked while pending review/approval
    setSelectedRow(row);
    setDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await crudeApi.delete(`${ENDPOINT}/${selectedRow.id}`);
      toast.success('Reception Facility has been deleted');
    } catch (error) {
      toast.error(`Failed to delete data: ${error}`);
    } finally {
      setDeleteModal(false);
      setSelectedRow(null);
      fetchData();
    }
  };

  // Server-side sort: only fires for columns that carry a `sortField`.
  const handleSort = (column, sortDirection) => {
    if (!column?.id) return;
    setSortField(toPascalCase(column.id));
    setSortOrder(sortDirection === 'desc' ? 'Desc' : 'Asc');
    setPage(1);
  };

  const headerLabel = label => (
    <span className='form-label text-uppercase fw-semibold fs-13 mb-0'>
      {label}
    </span>
  );

  const columns = [
    {
      id: 'plantName',
      name: headerLabel('Plant'),
      selector: row => row.plantName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'facilityName',
      name: headerLabel('Facility Name'),
      selector: row => row.facilityName,
      sortable: true,
      wrap: true,
    },
    {
      id: 'maxPourPoint',
      name: headerLabel('Max Pour Point'),
      selector: row => row.maxPourPoint,
      sortable: true,
      width: '160px',
      center: true,
      cell: row =>
        row.maxPourPoint === '' ||
        row.maxPourPoint === null ||
        row.maxPourPoint === undefined
          ? ''
          : Number(row.maxPourPoint).toFixed(2),
    },
    {
      id: 'orderNumber',
      name: headerLabel('Order Number'),
      selector: row => row.orderNumber,
      sortable: true,
      width: '140px',
      center: true,
    },
    {
      id: 'description',
      name: headerLabel('Description'),
      selector: row => row.description,
      wrap: true,
      grow: 2,
    },
    {
      name: headerLabel('Action'),
      width: '120px',
      center: true,
      cell: row => {
        const locked = row.dataStatus !== 'Active';
        const title = locked ? 'Sedang dalam review & approval' : undefined;
        return (
          <div className='d-flex gap-1 justify-content-end'>
            {/* disabled buttons swallow the native title tooltip, so the
                hover target moves to a wrapping span when locked */}
            <span
              title={title}
              style={locked ? { cursor: 'not-allowed' } : undefined}
            >
              <ActionButton
                variant='icon'
                size='sm'
                icon='ri-edit-line'
                label='Edit'
                onClick={() => handleUpdateModal(row)}
                disabled={locked}
                style={
                  locked ? { opacity: 0.5, pointerEvents: 'none' } : undefined
                }
              />
            </span>
            <span
              title={title}
              style={locked ? { cursor: 'not-allowed' } : undefined}
            >
              <ActionButton
                variant='icon'
                size='sm'
                danger
                icon='ri-delete-bin-line'
                label='Delete'
                confirm={false}
                onClick={() => handleDeleteModal(row)}
                disabled={locked}
                style={
                  locked ? { opacity: 0.5, pointerEvents: 'none' } : undefined
                }
              />
            </span>
          </div>
        );
      },
    },
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <DeleteModal
          show={deleteModal}
          onDeleteClick={handleDelete}
          onCloseClick={() => setDeleteModal(false)}
        />
        <Container fluid>
          <BreadCrumb title='Reception Facilities' pageTitle='CRUDE' />

          <Row>
            <Col lg={12}>
              <Card id='ReceptionFacilitiesList'>
                <CardHeader className='border-0'>
                  <div className='d-flex align-items-center flex-wrap gap-2'>
                    <h5 className='card-title mb-0 flex-grow-1'>
                      Reception Facilities
                    </h5>
                    <div className='flex-shrink-0'>
                      <div className='d-flex gap-2 flex-wrap'>
                        <div className='search-box'>
                          <Input
                            type='text'
                            className='form-control form-control-sm'
                            placeholder='Search...'
                            value={searchKeyword}
                            onChange={e => setSearchKeyword(e.target.value)}
                          />
                        </div>
                        <ActionButton
                          variant='primary'
                          size='sm'
                          icon='ri-play-list-add-line'
                          onClick={handleNewModal}
                        >
                          <span className='d-none d-sm-inline'>
                            Add Reception Facility
                          </span>
                          <span className='d-sm-none'>New</span>
                        </ActionButton>
                      </div>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className='pt-0'>
                  <div
                    className='table-responsive'
                    style={{
                      minHeight: 'auto',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    <DataTable
                      columns={columns}
                      highlightOnHover
                      data={items}
                      progressPending={loading}
                      pagination
                      paginationServer
                      paginationTotalRows={totalCount}
                      paginationDefaultPage={page}
                      paginationPerPage={pageSize}
                      paginationRowsPerPageOptions={[10, 25, 50, 100]}
                      onChangePage={p => setPage(p)}
                      onChangeRowsPerPage={(newPerPage, p) => {
                        setPageSize(newPerPage);
                        setPage(p);
                      }}
                      sortServer
                      onSort={handleSort}
                      wrap
                      dense
                      striped
                      fixedHeader={false}
                      noDataComponent={
                        <div
                          style={{
                            padding: '20px',
                            textAlign: 'center',
                            color: '#6c757d',
                          }}
                        >
                          <i
                            className='ri-database-2-line'
                            style={{
                              fontSize: '32px',
                              marginBottom: '8px',
                              display: 'block',
                            }}
                          ></i>
                          No reception facility found
                        </div>
                      }
                      customStyles={{
                        header: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            color: '#333',
                            fontWeight: 'bold',
                          },
                        },
                        headCells: {
                          style: {
                            backgroundColor: '#f0f0f0',
                            fontWeight: 'bold',
                            fontSize: '14px',
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            borderRight: '1px solid #dee2e6',
                            position: 'relative',
                            overflow: 'visible',
                          },
                        },
                        cells: {
                          style: {
                            paddingLeft: '12px',
                            paddingRight: '12px',
                            wordBreak: 'break-word',
                            borderRight: '1px solid #dee2e6',
                          },
                        },
                        rows: {
                          style: {
                            minHeight: '50px',
                            '&:hover': {
                              backgroundColor: '#f8f9fa',
                            },
                          },
                        },
                        table: {
                          style: {
                            width: '100%',
                            minWidth: '100%',
                          },
                        },
                      }}
                    />

                    <ToastContainer closeButton={false} limit={1} />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Modal
            isOpen={modalForm}
            role='dialog'
            autoFocus={true}
            centered
            id='addReceptionFacilityModal'
            toggle={() => setModalForm(!modalForm)}
            data-bs-backdrop='static'
            size='md'
          >
            <ModalHeader
              className='bg-soft-info'
              toggle={() => setModalForm(!modalForm)}
            >
              <span className='modal-title text-info fw-bold'>
                {formData.id !== null
                  ? 'Edit Reception Facility'
                  : 'Add Reception Facility'}
              </span>
            </ModalHeader>

            <ModalBody>
              <div className='d-flex mt-2'>
                <Col sm={12}>
                  <Row>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='facilityName' className='form-label'>
                          Facility Name <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='text'
                          id='facilityName'
                          name='facilityName'
                          className='form-control'
                          maxLength={250}
                          invalid={!!errors.facilityName}
                          value={formData.facilityName || ''}
                          onChange={handleChange}
                        />
                        {errors.facilityName && (
                          <div className='invalid-feedback d-block'>
                            {errors.facilityName}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='plantId' className='form-label'>
                          Refinery Unit <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='select'
                          id='plantId'
                          name='plantId'
                          className='form-control'
                          invalid={!!errors.plantId}
                          value={formData.plantId || ''}
                          onChange={handleChange}
                        >
                          <option value=''>Select Refinery Unit</option>
                          {refineryUnitOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>
                              {opt.name}
                            </option>
                          ))}
                        </Input>
                        {errors.plantId && (
                          <div className='invalid-feedback d-block'>
                            {errors.plantId}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='maxPourPoint' className='form-label'>
                          Max Pour Point <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='number'
                          id='maxPourPoint'
                          name='maxPourPoint'
                          className='form-control'
                          step='0.01'
                          invalid={!!errors.maxPourPoint}
                          value={formData.maxPourPoint ?? ''}
                          onChange={handleChange}
                        />
                        {errors.maxPourPoint && (
                          <div className='invalid-feedback d-block'>
                            {errors.maxPourPoint}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='orderNumber' className='form-label'>
                          Order Number <span className='text-danger'>*</span>
                        </Label>
                        <Input
                          type='number'
                          id='orderNumber'
                          name='orderNumber'
                          className='form-control'
                          min={1}
                          invalid={!!errors.orderNumber}
                          value={formData.orderNumber ?? ''}
                          onChange={handleChange}
                        />
                        {errors.orderNumber && (
                          <div className='invalid-feedback d-block'>
                            {errors.orderNumber}
                          </div>
                        )}
                      </div>
                    </Col>
                    <Col sm={12}>
                      <div className='mb-3'>
                        <Label for='description' className='form-label'>
                          Description
                        </Label>
                        <Input
                          type='text'
                          id='description'
                          name='description'
                          className='form-control'
                          value={formData.description || ''}
                          onChange={handleChange}
                        />
                      </div>
                    </Col>
                  </Row>
                </Col>
              </div>
            </ModalBody>

            <ModalFooter>
              <ActionButton
                variant='secondary'
                size='sm'
                icon='ri-close-line'
                onClick={() => setModalForm(false)}
                disabled={saving}
              >
                Cancel
              </ActionButton>
              <ActionButton
                variant='primary'
                size='sm'
                icon='ri-save-line'
                onClick={handleSave}
                loading={saving}
              >
                {saving ? 'Saving...' : 'Save'}
              </ActionButton>
            </ModalFooter>
          </Modal>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ReceptionFacilitiesView;
